import { useMemo } from 'react';
import { useSnackbar } from 'notistack';
import {
  QueryCache,
  useMutation,
  QueryClient,
  useQueryClient,
  UseMutationResult,
  MutationCache,
  Query
} from '@tanstack/react-query';
import type { Mutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { axiosHelper } from '@setvi/shared/services';
import { AxiosMethods, CommonApi } from '@setvi/shared/enums';
import { getStoredToken } from '@setvi/shared/utils/auth';
import { SlackAPIErrorBody } from '@setvi/shared/services/react-query/mutation/common/types';

/**
 * While there is no token in storage nobody is signed in: whatever a query
 * or mutation fails with is the tail of a logout (this tab or another one
 * sharing the storage), not something to toast or report to Slack
 * (DEV-1150). The pages reachable without a token (login, SSO callback) do
 * not use React Query, so nothing real is hidden.
 */
export const isLoggedOutRejection = () => !getStoredToken();

const ignoreErrorHandler = (error: AxiosError<any>) => {
  if (error?.code === 'ERR_CANCELED') return true;
  if (!error?.config) return false;

  const { baseURL } = error?.config;
  const status = error?.status;

  const apis = [
    process.env.REVOPS_API,
    process.env.REVOPS_API,
    process.env.INTELLIGENCE_EXPERIMENTAL_API
  ];
  if (status === 404 && apis.includes(baseURL)) return true;

  return false;
};

/**
 * Opt-out for a mutation that renders its own error copy.
 *
 * Set `meta: { ownErrorMessage: true }` on the mutation. The Slack report is
 * still sent -- only the raw-server-message snackbar is skipped, so the
 * feature's own message is not shadowed by a second, less useful toast.
 */
const hasOwnErrorMessage = (mutation?: Mutation<unknown, unknown, unknown>) =>
  !!mutation?.meta?.ownErrorMessage;

/** Fire-and-forget mutations (analytics) opt out of all error reporting. */
const ignoresErrors = (mutation?: Mutation<unknown, unknown, unknown>) =>
  !!mutation?.meta?.ignoreErrors;

const extractErrorMessage = (error: AxiosError<any>) => {
  if (!error?.response) return 'Something went wrong, please try again later';

  const data =
    error?.response?.data?.message ||
    error?.response?.data?.Message ||
    error?.response?.data;

  if (data && typeof data === 'string') return data;

  if (typeof data?.errors === 'object') {
    const errorKey = Object.keys(data.errors)[0];
    return data.errors[errorKey][0];
  }

  if (
    (data?.Error && typeof data?.Error === 'string') ||
    (data?.error && typeof data?.error === 'string')
  )
    return data?.Error || data?.error;

  if (
    (data?.Error?.Message && typeof data?.Error?.Message === 'string') ||
    (data?.error?.message && typeof data?.error?.message === 'string')
  )
    return data?.Error?.Message || data?.error?.message;

  return error?.message || 'Something went wrong, please try again later';
};

const sendAPIErrorToSlackChannel = async (errorData: SlackAPIErrorBody) => {
  try {
    await axiosHelper({
      endpoint: CommonApi.SlackChannelAPIError,
      method: AxiosMethods.POST,
      baseURL: process.env.REVOPS_API,
      data: errorData
    });
  } catch {
    // Invalid API
  }
};

const tranformError = (error: AxiosError<any>) => {
  const localUser = localStorage?.getItem('user');
  let user = null;

  if (localUser) {
    try {
      const parsedUser = JSON.parse(localUser);
      if (parsedUser) {
        user = {
          email: parsedUser.Email,
          userId: parsedUser?.ID,
          company: parsedUser?.CompanyID?.toString(),
          fullName: parsedUser?.FullName
        };
      }
    } catch {
      // Invalid JSON in localStorage, continue with user = null
    }
  }

  return {
    baseUrl: error?.config?.baseURL || '',
    code: error?.code || '',
    method: error?.config?.method || '',
    params: JSON.stringify(error?.config?.params) || '',
    endpoint: error?.config?.url || '',
    body: error?.config?.data || '',
    error: extractErrorMessage(error),
    user
  };
};

export function useReactQuery() {
  const { enqueueSnackbar } = useSnackbar();

  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false
          }
        },
        queryCache: new QueryCache({
          onError: (error: AxiosError, query: Query) => {
            if (isLoggedOutRejection()) return;

            const body = tranformError(error);
            sendAPIErrorToSlackChannel(body);

            if (ignoreErrorHandler(error)) return;
            enqueueSnackbar(extractErrorMessage(error), {
              variant: 'error',
              details: error?.response?.data || error?.message,
              autoHideDuration: 8000
            });

            // expect error
            // eslint-disable-next-line no-console
            console.error(query);
          }
        }),
        mutationCache: new MutationCache({
          onError: (
            error: unknown,
            _variables: unknown,
            _context: unknown,
            mutation: Mutation<unknown, unknown, unknown>
          ) => {
            if (ignoresErrors(mutation)) return;

            const axiosError = error as AxiosError;
            if (isLoggedOutRejection()) return;

            const body = tranformError(axiosError);
            sendAPIErrorToSlackChannel(body);

            if (ignoreErrorHandler(axiosError)) return;
            if (hasOwnErrorMessage(mutation)) return;

            enqueueSnackbar(extractErrorMessage(axiosError), {
              variant: 'error',
              details: axiosError?.response?.data || axiosError?.message,
              autoHideDuration: 8000
            });
          }
        })
      }),
    [enqueueSnackbar]
  );

  return {
    queryClient
  };
}

export function useSubscribedMutation(
  props: any,
  queries?: string[]
): UseMutationResult<any> {
  const queryClient = useQueryClient();

  return useMutation({
    ...props,
    onSuccess: () => {
      queries?.forEach((query: string) => {
        queryClient.invalidateQueries({
          queryKey: [query]
        });
      });
    }
  });
}
